import * as React from "react";
import { useState } from "react";
import { ServiceItem } from "../../calcBusterTypes";
import PropTypes from "prop-types";

import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  COLUMN_DEFINITIONS,
  EmptyState,
  collectionPreferencesProps,
  convertToColumnProps,
  getMatchesCountText,
} from "./util";
import {
  Box,
  Button,
  CollectionPreferences,
  Grid,
  Header,
  Pagination,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import { USDollar } from "../../util";

interface PreferencesType {
  pageSize: number;
  visibleContent: string[];
}

const ids = [
  "serviceCode",
  "description",
  "group",
  "region",
  "modernization",
  "map",
  "recommendation",
  "monthlyCost",
];

const Services: React.FC<{
  services: ServiceItem[];
}> = ({ services }) => {
  if (!services || services.length === 0) {
    return "No services";
  }

  const [preferences, setPreferences] = useState<PreferencesType>({
    pageSize: 10,
    visibleContent: [...ids],
  });

  //   const [expandedItems, setExpandedItems] = useState([])

  const {
    items,
    actions,
    filteredItemsCount = 0,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(services, {
    filtering: {
      empty: <EmptyState title="No Services" />,
      noMatch: (
        <EmptyState
          title="No matches"
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: "monthlyCost",
        },
        isDescending: true,
      },
    },
    selection: {},
  });

  const { selectedItems = [] } = collectionProps || {};

  const paginationLabels = {
    nextPageLabel: "Next page",
    pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
    previousPageLabel: "Previous page",
  };

  return (
    <Table
      {...collectionProps}
      selectionType="multi"
      header={
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Header
            counter={
              selectedItems.length
                ? `(${selectedItems.length}/${services.length})`
                : `(${services.length})`
            }
          >
            Service Estimates
          </Header>
        </Grid>
      }
      variant="embedded"
      stickyHeader
      wrapLines
      columnDefinitions={COLUMN_DEFINITIONS}
      columnDisplay={convertToColumnProps(preferences.visibleContent, ids)}
      items={items}
      //   expandableRows={{
      //     getItemChildren: item => item.subServices ?? [],
      //     isItemExpandable: item => Boolean(item.subServices?.length),
      //     expandedItems,
      //     onExpandableItemToggle: ({ detail }) => {
      //       const { item, expanded } = detail
      //       setExpandedItems(prevItems => {
      //         const items = prevItems.filter(i => i.id !== item.id)
      //         if (expanded) {
      //           items.push(item)
      //         }
      //         return items
      //       })
      //     },
      //   }}
      //   renderExpandableContent={({ item }) => {
      //     if (!item.subServices?.length) return null

      //     return (
      //       <Box padding={{ left: 'l' }}>
      //         <Table
      //           variant='embedded'
      //           columnDefinitions={COLUMN_DEFINITIONS}
      //           items={item.subServices}
      //           trackBy='id'
      //           empty={
      //             <Box textAlign='center' color='inherit'>
      //               <b>No sub-services</b>
      //             </Box>
      //           }
      //           header={<Header variant='h3'>Sub-services</Header>}
      //         />
      //       </Box>
      //     )
      //   }}
      pagination={
        <Pagination {...paginationProps} ariaLabels={paginationLabels} />
      }
      filter={
        <TextFilter
          {...filterProps}
          countText={getMatchesCountText(filteredItemsCount)}
          filteringAriaLabel="Filter services"
        />
      }
      preferences={
        <CollectionPreferences
          {...collectionPreferencesProps}
          preferences={preferences}
          onConfirm={({ detail }) => {
            const updatedPreferences: PreferencesType = {
              pageSize: detail.pageSize ?? preferences.pageSize,
              visibleContent: detail.visibleContent
                ? [...detail.visibleContent]
                : preferences.visibleContent,
            };
            setPreferences(updatedPreferences);
          }}
        />
      }
      footer={
        selectedItems.length > 0 && (
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <Box>
              <strong>Total Selected</strong>
            </Box>
            <Box
              float="right"
              variant="p"
              margin={{ right: "xxl" }}
              padding={{ right: "xxl" }}
            >
              {USDollar.format(
                selectedItems
                  .map((item) => item.serviceCost)
                  .reduce((total, item) => total + item, 0)
              )}
            </Box>
          </Grid>
        )
      }
    />
  );
};

Services.propTypes = {
  services: PropTypes.array.isRequired,
};

export default Services;
